import { useLiveQuery } from 'dexie-react-hooks';
import { coverageForAll } from '@/engine/coverage';
import { scaleNutrients, sumNutrients } from '@/engine/portions';
import type {
  Food,
  MealEntry,
  MealSlot,
  NutrientKey,
  NutrientValues,
  Targets,
} from '@/engine/types';
import { db, newId, nowIso, putAndQueue, softDeleteAndQueue, type MealRow } from '@/lib/db';
import { addDays } from '@/lib/format';

export const SLOTS: { value: MealSlot; label: string }[] = [
  { value: 'desayuno', label: 'Desayuno' },
  { value: 'comida', label: 'Comida' },
  { value: 'merienda', label: 'Merienda' },
  { value: 'cena', label: 'Cena' },
  { value: 'snacks', label: 'Snacks' },
];

export function useDayEntries(date: string): MealRow[] | undefined {
  return useLiveQuery(async () => {
    const rows = await db.meals.where('date').equals(date).toArray();
    return rows.filter((r) => r.deletedAt === null);
  }, [date]);
}

export interface DayTotals {
  totals: NutrientValues;
  coverage: Record<NutrientKey, number>;
  entries: MealRow[];
}

export function useDayTotals(date: string): DayTotals | undefined {
  const entries = useDayEntries(date);
  if (!entries) return undefined;

  const scaled = entries.map((e) => scaleNutrients(e.per100g, e.grams));
  return {
    totals: sumNutrients(scaled),
    coverage: coverageForAll(entries),
    entries,
  };
}

export async function addMealEntry(input: {
  date: string;
  slot: MealSlot;
  food: Pick<Food, 'id' | 'name' | 'per100g' | 'sources'>;
  grams: number;
}): Promise<string> {
  const id = newId();
  const entry: MealEntry = {
    id,
    date: input.date,
    slot: input.slot,
    foodId: input.food.id,
    foodName: input.food.name,
    grams: input.grams,
    // Copia congelada: si mañana cambia la ficha del alimento, lo ya registrado
    // no se mueve. SPEC 4.2.
    per100g: { ...input.food.per100g },
    sources: { ...input.food.sources },
  };

  await putAndQueue('meals', id, { ...entry, updatedAt: nowIso(), deletedAt: null });
  return id;
}

export async function removeMealEntry(id: string): Promise<void> {
  await softDeleteAndQueue('meals', id);
}

export async function updateMealGrams(id: string, grams: number): Promise<void> {
  const row = await db.meals.get(id);
  if (!row) return;
  await putAndQueue('meals', id, { ...row, grams, updatedAt: nowIso() });
}

/** Alimentos usados últimamente, del más reciente al más antiguo. */
export function useRecentFoodIds(limit = 12): string[] {
  const rows = useLiveQuery(
    async () => db.meals.orderBy('updatedAt').reverse().limit(200).toArray(),
    [],
    [] as MealRow[],
  );

  const seen: string[] = [];
  for (const row of rows ?? []) {
    if (row.deletedAt !== null) continue;
    if (!seen.includes(row.foodId)) seen.push(row.foodId);
    if (seen.length >= limit) break;
  }
  return seen;
}

/** Copia todos los registros de un día en otro. Para "repetir la comida de ayer". */
export async function repeatDay(
  fromDate: string,
  toDate: string,
  slot?: MealSlot,
): Promise<number> {
  const rows = await db.meals.where('date').equals(fromDate).toArray();
  const alive = rows.filter((r) => r.deletedAt === null && (!slot || r.slot === slot));

  for (const row of alive) {
    const id = newId();
    await putAndQueue('meals', id, {
      ...row,
      id,
      date: toDate,
      updatedAt: nowIso(),
      deletedAt: null,
    });
  }
  return alive.length;
}

export function yesterdayOf(date: string): string {
  return addDays(date, -1);
}

/**
 * Marca o desmarca el día como completo (SPEC 6.C).
 *
 * Al cerrarlo se congelan los objetivos vigentes: el histórico juzga cada día
 * con los objetivos que tenía entonces, no con los de hoy. Cambiar de
 * «mantener» a «perder» no puede convertir en excesos dos meses que estuvieron
 * bien (SPEC 4.2, mismo principio que los nutrientes del registro).
 */
export async function setDayComplete(
  date: string,
  complete: boolean,
  targets?: Targets,
): Promise<void> {
  const existing = await db.days.get(date);
  await putAndQueue('days', date, {
    ...(existing ?? { date, deletedAt: null }),
    date,
    complete,
    targets: complete ? (targets ?? existing?.targets ?? null) : (existing?.targets ?? null),
    updatedAt: nowIso(),
    deletedAt: null,
  });
}

export function useDayComplete(date: string): boolean {
  const row = useLiveQuery(async () => (await db.days.get(date)) ?? null, [date]);
  return row?.complete ?? false;
}

/** Franja por defecto segun la hora: casi siempre acierta y ahorra un toque. */
export function defaultSlot(now = new Date()): MealSlot {
  const h = now.getHours();
  if (h < 11) return 'desayuno';
  if (h < 16) return 'comida';
  if (h < 19) return 'merienda';
  if (h < 23) return 'cena';
  return 'snacks';
}
